import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  Chip,
  IconButton,
  Pagination,
  Paper,
  Tooltip,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchFiltersforReport,
  fetchOutstandingReportData,
  fetchSearchedEpisodes,
} from "./redux/ReportsSlice";
import ClearIcon from "@mui/icons-material/Clear";
import assignIcon from "../../../common/assets/images/reportAssign.png";
import HealthInformationManagerAssign from "../HealthInsuranceManager/HealthInformationManagerAssignModal.js";
import {
  queueOptions,
  updateQueuePageNumber,
  updateUserPageNumber,
  usersOptions,
} from "../HealthInsuranceManager/redux/HealthInsuranceManagerSlicer.js";

const ReportTable = ({
  episodes,
  setEpisodes,
  outstandingPageNumber,
  setOutstandingPageNumber,
  setSelectedFilters,
  selectedFilters,
  searchText,
  setSearchText,
}) => {
  const dispatch = useDispatch();
  const [totalPages, setTotalPages] = useState(1);
  const [assignUserModal, setAssignUserModal] = useState(false);
  const [uncheckedIds, setUncheckedIds] = useState([]);
  const { usersPageNumber, usersLoading, isUserPageTrue } = useSelector(
    (state) => state.healthInsuranceManager.users
  );
    const {
      queuePageNumber,
      queueData,
      queueLoading,
      queueError,
      isQueuePageTrue,
    } = useSelector((state) => state.healthInsuranceManager.queue);
  const [selectedRecord, setSelectedRecord] = useState({
    hcp_line_id: null,
    assigned_user: "",
    queue: "",
  });
  const outstandingReportTableData = useSelector(
    (state) => state.outstandingreport.outstandingReportTableData
  );
  const loadingOutstandingReportTableData = useSelector(
    (state) => state.outstandingreport.loadingOutstandingReportTableData
  );
  const loadingsearchedDataForReport = useSelector(
    (state) => state.outstandingreport.loadingsearchedDataForReport
  );
  useEffect(() => {
    const fetchData = async () => {
      const data = await dispatch(
        fetchOutstandingReportData({
          pageNumber: parseInt(outstandingPageNumber),
          selectedFilters: { ...selectedFilters, search_term: searchText },
        })
      );
      if (data?.payload?.total_pages) {
        setTotalPages(data.payload.total_pages);
      }
    };
    fetchData();
  }, [dispatch, outstandingPageNumber, selectedFilters, searchText]);
  useEffect(() => {
    // whenever filters/search change, reset to 1
    setOutstandingPageNumber(1);
  }, [selectedFilters, searchText]);
  useEffect(() => {
    dispatch(fetchFiltersforReport());
  }, []);
  useEffect(() => {
    if (outstandingReportTableData?.total_pages) {
      setTotalPages(outstandingReportTableData?.total_pages);
    }
  }, [outstandingReportTableData]);

  // const data = useMemo(() => outstandingReportTableData.flat(),[outstandingReportTableData]);
  useEffect(() => {
    if (Array.isArray(outstandingReportTableData?.episodes)) {
      setEpisodes(outstandingReportTableData.episodes);
    } else {
      setEpisodes([]); // fallback
    }
  }, [outstandingReportTableData]);

  const columns = [
    {
      accessorKey: "priority",
      header: "Priority",
      // size: 80,
      Cell: ({ cell }) => {
        const value = cell.getValue() ?? "-";
        const formatted =
          typeof value === "string" && value.length > 0
            ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
            : null;
        const styleMap = {
          Urgent: { background: "#E5F5F4", color: "#26625E" }, // red tone
          High: { background: "#FDE8E8", color: "#9B1C1C" }, // orange tone
          Medium: { background: "#FDF6B2", color: "#723B13" }, // yellow tone
          Low: { background: "#d6f5d6", color: "#26625E" }, // green tone
          null: { background: "none" },
        };
        if (!formatted) {
          return <span>-</span>;
        }
        const styles = styleMap[formatted] || {};
        return (
          <span
            style={{
              borderRadius: "4px",
              padding: "4px 8px",
              ...styles,
            }}
          >
            {formatted}
          </span>
        );
      },
    },
    {
      accessorKey: "episode_identifier",
      header: "Episode ID",
      // size: 250,
      Cell: ({ cell }) => {
        const value = cell.getValue() ?? "-";
        return (
          <Tooltip title={value} arrow>
            <span>{value}</span>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "patient_id",
      header: "Patient ID",
      // size: 80,
      Cell: ({ cell }) => {
        const value = cell.getValue() ?? "-";
        return (
          <Tooltip title={value} arrow>
            <span>{value}</span>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "speciality",
      header: "Speciality",
      // size: 80,
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      accessorKey: "admission_date",
      header: "Admission Date",
      // size: 80,
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      accessorKey: "discharge_date",
      header: "Discharge Date",
      // size: 80,
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      accessorKey: "los",
      header: "LOS",
      // size: 80,
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      accessorKey: "days_of_outstanding",
      header: "Days Outstanding",
      // size: 80,
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      accessorKey: "assign_coder",
      header: "Assigned Coder",
      // size: 80,
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 200, // give it some fixed width
      Cell: ({ cell }) => {
        const value = cell.getValue() ?? "-";

        let dotColor = "";
        if (value === "Outstanding") dotColor = "#FF6B6B";
        else if (
          value === "AI processing complete - codes available for validation"
        )
          dotColor = "#4CAF50";

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              border: "1px solid #D0D5DD",
              padding: "4px 8px",
              boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
              borderRadius: "4px",
              maxWidth: "180px",
              overflow: "hidden",
            }}
          >
            {/* Dot */}
            <Box
              sx={{
                flex: "0 0 auto",
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: dotColor,
                // border: "1px solid #555",
              }}
            />
            {/* Text with ellipsis */}
            <Tooltip title={value} arrow>
              <Box
                component="span"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  flex: "1 1 auto",
                }}
              >
                {value}
              </Box>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      accessorKey: "assign_icon",
      header: "",
      size: 80,
      enableSorting: false,
      Cell: ({ row }) => (
        <Tooltip title="Assign">
          <img
            src={assignIcon}
            alt="assign-icon"
            style={{ cursor: "pointer" }}
            onClick={() => {
              setSelectedRecord({
                hcp_line_id: row.original.hcp_line_id, // from row data
                assigned_user: row.original.assign_coder ?? "",
                queue: row.original.current_queue ?? "",
              });
              setAssignUserModal(true);
            }}
          />
        </Tooltip>
      ),
    },
  ];
  const handleSearchChange = useCallback(
    (value) => {
      setSearchText(value);

      if (value?.length > 0) {
        dispatch(
          fetchOutstandingReportData({
            pageNumber: outstandingPageNumber,
            searchTerm: value,
            ...selectedFilters,
          })
        )
          .unwrap()
          .then((res) => {
            setEpisodes(res?.episodes);
          })
          .catch((err) => {
            console.error("Search error:", err);
          });
      } else {
        setEpisodes(outstandingReportTableData?.episodes);
      }
    },
    [dispatch, outstandingReportTableData]
  );
  const PaginationFooter = React.memo(({ totalPages, page, onChange }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        width: "100%",
        padding: "8px 16px",
      }}
    >
      <Pagination
        count={totalPages}
        page={page}
        onChange={(_, newPage) => onChange(newPage)}
        siblingCount={2}
        boundaryCount={1}
        showFirstButton
        showLastButton
        color="primary"
        shape="rounded"
      />
    </Box>
  ));

  const table = useMaterialReactTable({
    columns,
    data: episodes ?? [],
    manualPagination: true,
    manualFiltering: true,
    enableDensityToggle:false,
    enableColumnFilters:false,
    initialState: { density: "compact" },
    pageCount: totalPages,

    state: {
      isLoading:
        loadingOutstandingReportTableData || loadingsearchedDataForReport,
      pagination: { pageIndex: outstandingPageNumber - 1, pageSize: 10 },
      globalFilter: searchText,
    },

    onPaginationChange: (updater) => {
      setOutstandingPageNumber(
        typeof updater === "function"
          ? updater(outstandingPageNumber - 1) + 1
          : updater.pageIndex + 1
      );
    },
    enablePagination: false, // disable MRT built-in
    onGlobalFilterChange: handleSearchChange,
    muiSearchTextFieldProps: {
      placeholder: "Search by Episode ID...",
      variant: "outlined",
      size: "small",
      InputProps: {
        endAdornment: (
          <IconButton size="small" onClick={() => handleSearchChange("")}>
            <ClearIcon fontSize="small" />
          </IconButton>
        ),
      },
    },
    muiTableBodyProps: {
      sx: {
        "& tr:nth-of-type(odd)": { backgroundColor: "#fafafa" },
        "& tr:hover": { backgroundColor: "#f0f7ff" },
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#f2f6fc !important",
        "& .Mui-TableHeadCell-Content": {
          backgroundColor: "#f2f6fc !important",
        },
        padding: "4px 12px",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        padding: "4px 12px",
      },
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "#f2f6fc !important",
      },
    },

    muiTableContainerProps: {
      sx: {
        maxHeight: 500,
        "& thead th": {
          position: "sticky",
          top: 0,
          zIndex: 1,
          backgroundColor: "#fff",
        },
      },
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: "#f2f6fc",
        padding: "8px 16px",
        // borderBottom: "1px solid #bbdefb",
      },
    },

    // Custom pagination footer
    renderBottomToolbarCustomActions: () => (
      <PaginationFooter
        totalPages={totalPages}
        page={outstandingPageNumber}
        onChange={setOutstandingPageNumber}
        showPageSizeOptions
        totalCount={episodes?.length ?? 0}
      />
    ),
  });

  const isFullScreen = table.getState().fullscreen;
  useEffect(() => {
    if (isUserPageTrue && !usersLoading) {
      dispatch(updateUserPageNumber(usersPageNumber + 1));
      dispatch(usersOptions(usersPageNumber + 1));
    }
  }, [isUserPageTrue, usersLoading]);
  useEffect(() => {
    if (isQueuePageTrue && !queueLoading) {
      dispatch(updateQueuePageNumber(queuePageNumber + 1));
      dispatch(queueOptions(queuePageNumber + 1));
    }
  }, [isQueuePageTrue, queueLoading]);
  return (
    <Paper
      className={`outstanding-report-table ${
        isFullScreen ? "fullscreen-active" : ""
      }`}
    >
      <>
        <MaterialReactTable
          table={table}
          enableDensityToggle
          columnSizingMode="fit"
          renderEmptyRowsFallback={() => (
            <div style={{ textAlign: "center", padding: "2rem" }}>
              No data available
            </div>
          )}
        />
        {assignUserModal && (
          <HealthInformationManagerAssign
            workflowHimsAssginModal={assignUserModal}
            setWorkflowHimsAssginModal={setAssignUserModal}
            selectedRecord={selectedRecord}
            uncheckedIds={uncheckedIds}
          />
        )}
      </>
    </Paper>
  );
};

export default ReportTable;
